import React from 'react';
import { X, Package, MapPin, Clock, CheckCircle, Loader } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { LocationItemInfo } from '../../../../../services/inventoryCorrectionService';

interface ItemSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (item: LocationItemInfo) => void;
  locationName: string;
  warehouseID: string;
  items: LocationItemInfo[];
  loading: boolean;
}

export const ItemSelectionModal: React.FC<ItemSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  locationName,
  warehouseID,
  items,
  loading
}) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  const handleSelect = (item: LocationItemInfo) => {
    onSelect(item);
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-4">
        <div className="bg-white w-full h-full sm:h-auto sm:rounded-lg shadow-xl sm:max-w-2xl sm:max-h-[80vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b flex-shrink-0">
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
              <MapPin className="h-5 w-5 sm:h-6 sm:w-6 text-red-500 flex-shrink-0" />
              <div className="min-w-0">
                <h2 className="text-base sm:text-lg font-semibold truncate">{t('warehouse.modules.inventoryCorrection.modals.selectItemForCorrection')}</h2>
                <p className="text-xs sm:text-sm text-gray-600 truncate">
                  {t('warehouse.general.location')}: {locationName} ({t('warehouse.general.warehouse')}: {warehouseID})
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full flex-shrink-0 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader className="animate-spin h-8 w-8 text-red-500" />
                <span className="ml-2 text-sm sm:text-base">{t('warehouse.modules.inventoryCorrection.messages.loadingItems')}</span>
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-8">
                <Package className="h-10 w-10 sm:h-12 sm:w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-sm sm:text-base text-gray-500">{t('warehouse.modules.inventoryCorrection.messages.noItemsFoundAtLocation')}</p>
                <p className="text-xs sm:text-sm text-gray-400 mt-1">
                  {t('warehouse.modules.inventoryCorrection.messages.locationHasNoRegisteredItems')}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-xs sm:text-sm text-gray-600 mb-4">
                  {t('warehouse.modules.inventoryCorrection.messages.locationHasItems', { '0': items.length, '1': items.length !== 1 ? 'r' : '' })}
                </div>

                {items.map((item, index) => (
                  <div
                    key={`${item.itemNo}-${index}`}
                    onClick={() => handleSelect(item)}
                    className="border border-gray-200 rounded-lg p-4 hover:border-red-300 hover:bg-red-50 cursor-pointer transition-all active:bg-red-100 min-h-[44px]"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-start sm:items-center space-x-3 min-w-0 flex-1">
                        <Package className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5 sm:mt-0" />
                        <div className="min-w-0">
                          <div className="font-semibold font-mono text-sm break-all">
                            {item.itemNo}
                          </div>
                          <div className="text-xs sm:text-sm text-gray-600 break-words mt-0.5">
                            {item.description}
                          </div>
                        </div>
                      </div>

                      <div className="text-left sm:text-right flex-shrink-0">
                        <div className={`font-semibold text-sm ${
                          item.availableQuantity > 0
                            ? 'text-green-600'
                            : item.availableQuantity === 0
                              ? 'text-gray-500'
                              : 'text-red-600'
                        }`}>
                          {item.availableQuantity} {t('warehouse.general.available')}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-500">
                          {item.totalQuantity} {t('warehouse.modules.inventoryCorrection.labels.total')}
                          {item.reservedQuantity > 0 && (
                            <span className="ml-1 text-orange-600">
                              ({item.reservedQuantity} {t('warehouse.general.reserved')})
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Additional info */}
                    <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-gray-500">
                      {item.binCodePrimary && (
                        <div className="flex items-center whitespace-nowrap">
                          <CheckCircle className="h-3 w-3 mr-1 text-green-500 flex-shrink-0" />
                          <span>{t('warehouse.modules.inventoryCorrection.labels.primaryLocation')}</span>
                        </div>
                      )}

                      {item.binCodeFixed && (
                        <div className="flex items-center whitespace-nowrap">
                          <span className="w-3 h-3 bg-blue-500 rounded-full mr-1 flex-shrink-0"></span>
                          <span>{t('warehouse.modules.inventoryCorrection.labels.fixedLocation')}</span>
                        </div>
                      )}

                      {item.lastMovementDate && (
                        <div className="flex items-center">
                          <Clock className="h-3 w-3 mr-1 flex-shrink-0" />
                          <span className="break-words">
                            {t('warehouse.modules.inventoryCorrection.labels.lastMoved')}: {item.lastMovementDate.toLocaleDateString('no-NO')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end p-4 sm:p-6 border-t space-x-3 flex-shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-2.5 sm:py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-sm min-h-[44px] sm:min-h-0"
            >
              {t('common.buttons.cancel')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};